import React, { useMemo, useState } from "react";
// oxlint-disable-next-line no-restricted-imports -- Legacy table migration/demo — do not copy into new code.
import {
    GridTable,
    GridTableHeader,
    GridTableRow,
} from "@design-system/Tables/GridTable/GridTable";
// oxlint-disable-next-line no-restricted-imports -- Legacy table migration/demo — do not copy into new code.
import { SimpleTable } from "@design-system/Tables/SimpleTable/SimpleTable";
import { SortableHeader } from "@design-system/SortableHeader/SortableHeader";
import { StickyTableFooter } from "@design-system/StickyTableFooter/StickyTableFooter";
import stickyFooterStyles from "@design-system/StickyTableFooter/StickyTableFooter.module.scss";
import { BooleanControl, VariantGrid, VariantItem } from "./playgroundControls";
import { SandboxLayout } from "./SandboxLayout";
import { ShowcasePreview } from "./ShowcasePreview";
import previewStyles from "./DatePickerPlayground.module.scss";
import styles from "./TablesPlayground.module.scss";

const transactions = [
    {
        id: "tx-1",
        date: "Aug 8, 2026",
        description: "Stripe payout",
        amount: 4820.35,
    },
    {
        id: "tx-2",
        date: "Aug 7, 2026",
        description: "AWS cloud services",
        amount: -684.12,
    },
    {
        id: "tx-3",
        date: "Aug 5, 2026",
        description: "Gusto payroll",
        amount: -2450,
    },
    {
        id: "tx-4",
        date: "Aug 2, 2026",
        description: "Client invoice payment",
        amount: 3200,
    },
];

const currencyFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
});

type AmountSort = "amountAsc" | "amountDesc";

export const TablesPlayground = () => {
    const [sort, setSort] = useState<AmountSort>("amountDesc");
    const [hasColumnBorders, setHasColumnBorders] = useState(false);
    const [doesHighlightRows, setDoesHighlightRows] = useState(true);
    const [isFooterSticky, setIsFooterSticky] = useState(true);
    const [isFooterBorderless, setIsFooterBorderless] = useState(false);

    const sortedTransactions = useMemo(
        () =>
            transactions.toSorted((firstTransaction, secondTransaction) =>
                sort === "amountAsc"
                    ? firstTransaction.amount - secondTransaction.amount
                    : secondTransaction.amount - firstTransaction.amount,
            ),
        [sort],
    );
    const total = transactions.reduce(
        (sum, transaction) => sum + transaction.amount,
        0,
    );
    const simpleColumns = [
        { key: "date", label: "Date" },
        { key: "description", label: "Description" },
        { key: "amount", label: "Amount" },
    ];
    const simpleRows = sortedTransactions.map((transaction) => ({
        id: transaction.id,
        cells: [
            { value: transaction.date },
            { value: transaction.description },
            { value: currencyFormatter.format(transaction.amount) },
        ],
    }));

    return (
        <SandboxLayout
            controls={
                <>
                    <section>
                        <h2>Table features</h2>
                        <div className={previewStyles.booleanGrid}>
                            <BooleanControl
                                label="Column borders"
                                checked={hasColumnBorders}
                                onChange={setHasColumnBorders}
                            />
                            <BooleanControl
                                label="Highlight grid rows on hover"
                                checked={doesHighlightRows}
                                onChange={setDoesHighlightRows}
                            />
                        </div>
                    </section>
                    <section>
                        <h2>Sticky footer</h2>
                        <div className={previewStyles.booleanGrid}>
                            <BooleanControl
                                label="Sticky"
                                checked={isFooterSticky}
                                onChange={setIsFooterSticky}
                            />
                            <BooleanControl
                                label="Borderless"
                                checked={isFooterBorderless}
                                onChange={setIsFooterBorderless}
                            />
                        </div>
                    </section>
                </>
            }
        >
            <ShowcasePreview
                summary="Sort by amount in the GridTable header"
                width="full"
                output={JSON.stringify(
                    {
                        SimpleTable: {
                            columnBorders: hasColumnBorders,
                        },
                        GridTable: {
                            highlightRowOnHover: doesHighlightRows,
                        },
                        StickyTableFooter: {
                            sticky: isFooterSticky,
                            borderless: isFooterBorderless,
                        },
                        showcaseState: { sort },
                    },
                    null,
                    2,
                )}
            >
                <VariantGrid title="Table families" columns="full">
                    <VariantItem label="SimpleTable">
                        <SimpleTable
                            className={styles.fullWidth}
                            columns={simpleColumns}
                            rows={simpleRows}
                            columnBorders={hasColumnBorders}
                        />
                    </VariantItem>
                    <VariantItem label="GridTable + SortableHeader + StickyTableFooter">
                        <div className={styles.tableFrame}>
                            <GridTable
                                className={styles.fullWidth}
                                gridTemplateColumns="140px minmax(240px, 1fr) 160px"
                                highlightRowOnHover={doesHighlightRows}
                            >
                                <GridTableHeader
                                    columnBorders={hasColumnBorders}
                                >
                                    <div>Date</div>
                                    <div>Description</div>
                                    <div>
                                        <SortableHeader
                                            text="Amount"
                                            sort={sort}
                                            asc="amountAsc"
                                            desc="amountDesc"
                                            defaultOrder="desc"
                                            onChange={setSort}
                                        />
                                    </div>
                                </GridTableHeader>
                                {sortedTransactions.map((transaction) => (
                                    <GridTableRow
                                        key={transaction.id}
                                        columnBorders={hasColumnBorders}
                                        cells={[
                                            {
                                                key: "date",
                                                value: transaction.date,
                                            },
                                            {
                                                key: "description",
                                                value: transaction.description,
                                            },
                                            {
                                                key: "amount",
                                                value: currencyFormatter.format(
                                                    transaction.amount,
                                                ),
                                                className: styles.amount,
                                            },
                                        ]}
                                    />
                                ))}
                            </GridTable>
                            <StickyTableFooter
                                sticky={isFooterSticky}
                                borderless={isFooterBorderless}
                            >
                                <div className={stickyFooterStyles.row}>
                                    <div
                                        className={
                                            stickyFooterStyles.rowContent
                                        }
                                    >
                                        <div className={styles.footerContent}>
                                            <span>
                                                {transactions.length}{" "}
                                                transactions
                                            </span>
                                            <strong>
                                                Net{" "}
                                                {currencyFormatter.format(
                                                    total,
                                                )}
                                            </strong>
                                        </div>
                                    </div>
                                </div>
                            </StickyTableFooter>
                        </div>
                    </VariantItem>
                </VariantGrid>
            </ShowcasePreview>
        </SandboxLayout>
    );
};
